/*
 * Trade application form (Brooks Creative Studio)
 * 1. Sends the application + documents to the Make.com webhook.
 * 2. Only counts it as done when Make replies {"status":"ok"} (sent at the END of the scenario).
 * 3. Otherwise, submits the text fields through Shopify's native contact form (page reloads
 *    to the success message; Shopify's invisible CAPTCHA runs as part of that submission).
 * The visitor sees the same success message either way.
 */
(() => {
  if (customElements.get('trade-application-form')) return;

  const WEBHOOK_TIMEOUT_MS = 45000;
  const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif'];

  class TradeApplicationForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-trade-form]');
      this.success = this.querySelector('[data-trade-success]');
      this.fallbackForm = this.querySelector('[data-trade-fallback] form');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.fileInputs = [...this.form.querySelectorAll('input[type="file"]')];
      this.maxBytes = (parseFloat(this.dataset.maxMb) || 5) * 1024 * 1024;
      this.busy = false;

      // A backup submission that had to go through Shopify's captcha page lands back here.
      if (this.querySelector('[data-backup-posted]')) {
        this.showSuccess();
        return;
      }

      this.fileInputs.forEach((input) => input.addEventListener('change', () => this.onFileChange(input)));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      // Coming back with the browser's Back button can restore the page mid-submit;
      // reset the button and re-sync the file names.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        if (this.busy) this.setBusy(false);
        this.fileInputs.forEach((input) => this.onFileChange(input));
      });
    }

    onFileChange(input) {
      const file = input.files[0];
      const box = input.closest('[data-trade-upload]');
      const nameEl = box.querySelector('[data-file-name]');
      let message = '';

      if (file) {
        const extension = file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          message = 'Please upload a PDF, JPG, PNG or HEIC file.';
        } else if (file.size > this.maxBytes) {
          message = `This file is larger than ${this.dataset.maxMb} MB. Please upload a smaller file or a compressed PDF.`;
        }
      }

      input.setCustomValidity(message);
      nameEl.textContent = file && !message ? `${input.dataset.uploadLabel}: ${file.name}` : '';
      box.toggleAttribute('data-has-file', Boolean(file && !message));

      if (message) {
        input.value = '';
        input.reportValidity();
        // Allow a fresh pick to re-validate cleanly.
        input.addEventListener('click', () => input.setCustomValidity(''), { once: true });
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      const data = new FormData(this.form);

      // Honeypot filled: usually a bot, but sometimes a real person whose browser autofilled it.
      // Never drop it silently. Send it through the backup email, flagged for review.
      const honeypotValue = data.get('hp_ref_zq');
      if (honeypotValue) {
        this.setBusy(true);
        await this.sendBackupEmail({ flagged: true, honeypotValue });
        this.setBusy(false);
        this.showSuccess();
        return;
      }
      data.delete('hp_ref_zq');

      // Send Shopify's own consent value so no conditional logic is needed downstream.
      const optIn = this.form.querySelector('[name="email_opt_in"]');
      data.set('email_opt_in', optIn && optIn.checked ? 'SUBSCRIBED' : 'NOT_SUBSCRIBED');

      // Shopify rejects blank metafield values, so send a placeholder for empty optional fields.
      for (const [key, value] of [...data.entries()]) {
        if (typeof value === 'string' && value.trim() === '') data.set(key, 'Not provided');
      }

      this.setBusy(true);

      let processed = false;
      const webhookUrl = this.dataset.webhookUrl;

      if (webhookUrl) {
        data.append('submitted_at', new Date().toISOString());
        data.append('page_url', window.location.href);
        processed = await this.sendToMake(webhookUrl, data);
      }

      if (!processed) {
        await this.sendBackupEmail();
      }

      this.setBusy(false);
      this.showSuccess();
    }

    async sendToMake(url, data) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), WEBHOOK_TIMEOUT_MS);

      try {
        const response = await fetch(url, { method: 'POST', body: data, signal: controller.signal });
        if (!response.ok) return false;
        const body = await response.json().catch(() => null);
        return Boolean(body && body.status === 'ok');
      } catch (error) {
        return false;
      } finally {
        clearTimeout(timer);
      }
    }

    async sendBackupEmail({ flagged = false, honeypotValue = '' } = {}) {
      if (!this.fallbackForm) return;

      const container = this.fallbackForm.querySelector('[data-fallback-fields]');
      container.replaceChildren();

      const add = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        container.appendChild(input);
      };

      const documents = this.fileInputs
        .map((input) => `${input.dataset.uploadLabel}: ${input.files[0] ? input.files[0].name : 'none selected'}`)
        .join('\n');

      const flagNote = flagged
        ? `POSSIBLE SPAM: the hidden anti-spam field was filled with "${honeypotValue}". ` +
          `If this looks like a real applicant, their browser's autofill most likely caused it. ` +
          `This application did NOT reach Shopify. Create the customer manually and ask them to email their documents.\n\n`
        : '';

      add('contact[body]', `${flagNote}${this.dataset.backupNotice}\n\nFiles the applicant selected (not attached):\n${documents}`);

      const optInField = this.form.querySelector('[name="email_opt_in"]');
      if (optInField) add('contact[Email opt-in]', optInField.checked ? 'Yes' : 'No');

      // Checkboxes are skipped here: their value is always "yes" whether ticked or not,
      // and the opt-in's real state is already added above.
      this.form.querySelectorAll('input:not([type="file"]):not([type="checkbox"]):not([name="hp_ref_zq"]), select').forEach((field) => {
        if (!field.name) return;
        const label = this.form.querySelector(`label[for="${field.id}"]`);
        const title = field.name === 'name' || field.name === 'email' ? field.name : (label ? label.textContent.trim() : field.name);
        add(`contact[${title}]`, field.value);
      });

      // Shopify's bot protection adds its CAPTCHA token when the form's submit event fires,
      // so the backup has to be a real form submission (requestSubmit), not a background fetch.
      // The page reloads and this section shows the success message.
      if (typeof this.fallbackForm.requestSubmit === 'function') {
        this.fallbackForm.requestSubmit();
      } else {
        this.fallbackForm.querySelector('[type="submit"]').click();
      }

      // Submitting normally navigates away. If the page is still here after 8 seconds
      // (Shopify blocked it, or the store is password-protected), show the success
      // message rather than leaving the visitor on "Sending...".
      await new Promise((resolve) => setTimeout(resolve, 8000));
      console.warn('Trade form: backup submission did not complete.');
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.submitButton) return;

      if (busy) {
        this.originalLabel = this.submitButton.innerHTML;
        if (this.dataset.busyLabel) this.submitButton.textContent = this.dataset.busyLabel;
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
      } else {
        this.submitButton.innerHTML = this.originalLabel;
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.disabled = false;
      }
    }

    showSuccess(scroll = true) {
      this.form.hidden = true;
      this.success.hidden = false;
      if (scroll) {
        this.success.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.success.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('trade-application-form', TradeApplicationForm);
})();